import useGetTranslation from "@/app/[tenantId]/[messengerId]/[hostConfig]/[locale]/lib/client/hooks/useGetTranslation";
import { useLauncherStore } from "@/app/[tenantId]/[messengerId]/[hostConfig]/lib/client/contexts/LauncherStoreContext";
import clsx from "clsx";
import { formatDistance } from "@/lib/client/utils/date";
import { useRef, useEffect, useCallback } from "react";
import { useIntl } from "react-intl";
import { useShallow } from "zustand/react/shallow";
import { Button } from "../ui/Button";
import { buttonTheme } from "../ui/Button/theme";
import { Icon } from "../ui/Icon";
import {
  getIframeDimensions,
  LAUNCHER_BUTTON_NO_PADDING,
  PADDING,
} from "@/app/[tenantId]/[messengerId]/[hostConfig]/lib/client/utils/getIframeDimensions";
import { useLauncherContext } from "@/app/[tenantId]/[messengerId]/[hostConfig]/lib/client/contexts/LauncherContext";

const CHAT_SIDE_MARGIN = 8;

export const ProactiveChat = ({
  side,
  isMessengerWidgetOpen,
  messengerId,
  offsetBottom,
  offsetSide,
  proactiveChatIsOpen,
  setProactiveChatIsOpen,
  proactiveChatWasShown,
  messengerWidgetWasOpen,
}: {
  side: "left" | "right";
  isMessengerWidgetOpen: boolean;
  messengerId: string;
  offsetBottom: number;
  offsetSide: number;
  proactiveChatIsOpen: boolean;
  setProactiveChatIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  proactiveChatWasShown: React.MutableRefObject<boolean>;
  messengerWidgetWasOpen: React.MutableRefObject<boolean>;
}) => {
  const { config, showLauncherButton } = useLauncherStore(
    useShallow((state) => ({
      showLauncherButton: state.showLauncherButton,
      config: state.config,
    })),
  );
  const { connection } = useLauncherContext();
  const delay = (config?.settings.launcherSettings.delayShowingMessage || 10) * 1000;
  const timeout = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const resizeIframe = useCallback(
    async (open: boolean) => {
      if (!connection?.current) return;
      if (isMessengerWidgetOpen) return;
      if (messengerWidgetWasOpen.current) return;

      const parentApi = await connection.current.promise;
      const parentViewDimensions = await parentApi.getViewDimensions(messengerId);

      const containerWidthPx = open
        ? (containerRef.current?.scrollWidth || 0) + CHAT_SIDE_MARGIN
        : 0;
      const containerHeightPx = open ? containerRef.current?.scrollHeight || 0 : 0;

      console.log("[dp-messenger] ProactiveChat resizeIframe", {
        containerWidthPx,
        containerHeightPx,
      });

      const data = getIframeDimensions({
        isOpen: false,
        showLauncherButton,
        offsetBottom,
        offsetSide,
        parentViewDimensions,
        proactiveChatDimensions: {
          width: containerWidthPx,
          height: containerHeightPx,
        },
      });

      parentApi.toggleProactiveChat(messengerId, {
        width: data.width,
        height: data.height,
        containerWidthPx,
        containerHeightPx,
      });
    },
    [
      connection,
      isMessengerWidgetOpen,
      messengerId,
      messengerWidgetWasOpen,
      offsetBottom,
      offsetSide,
      showLauncherButton,
    ],
  );

  useEffect(() => {
    if (isMessengerWidgetOpen) {
      setProactiveChatIsOpen(false);
      // If widget opens on init, prevent proactive chat displaying when the widget closes
      proactiveChatWasShown.current = true;
      window.clearTimeout(timeout.current);
    }
  }, [isMessengerWidgetOpen, setProactiveChatIsOpen, proactiveChatWasShown]);

  useEffect(() => {
    if (!isMessengerWidgetOpen && !proactiveChatIsOpen && !proactiveChatWasShown.current) {
      timeout.current = window.setTimeout(() => {
        setProactiveChatIsOpen(true);
        proactiveChatWasShown.current = true;
      }, delay);
    }

    return () => {
      if (timeout.current) {
        window.clearTimeout(timeout.current);
      }
    };
  }, [
    delay,
    isMessengerWidgetOpen,
    proactiveChatIsOpen,
    setProactiveChatIsOpen,
    proactiveChatWasShown,
  ]);

  useEffect(() => {
    resizeIframe(proactiveChatIsOpen);
    // Only trigger on proactiveChatIsOpen change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proactiveChatIsOpen]);

  const wrapperOffsetSide = LAUNCHER_BUTTON_NO_PADDING + PADDING + CHAT_SIDE_MARGIN;
  const wrapperOffsetBottom = PADDING;

  return isMessengerWidgetOpen ? undefined : (
    <div
      className={clsx(
        proactiveChatIsOpen
          ? "opacity-1 translate-y-0 transition-all delay-[300ms] duration-300"
          : "translate-y-2 opacity-0",
        "flex",
        "absolute",
        side === "left" ? `left-[${wrapperOffsetSide}px]` : `right-[${wrapperOffsetSide}px]`,
      )}
      style={{
        bottom: wrapperOffsetBottom,
        ...(side === "left" ? { left: wrapperOffsetSide } : { right: wrapperOffsetSide }),
      }}
      data-testid="proactive-chat"
    >
      <div
        className={clsx("flex justify-end gap-2", {
          "flex-row-reverse": side === "left",
        })}
        ref={containerRef}
      >
        <Button
          isIconButton
          size="xs"
          minimal
          pill
          className={clsx(
            "rounded-full shadow-md",
            buttonTheme.color.secondary.base,
            "border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800",
          )}
          animate={false}
          onClick={() => setProactiveChatIsOpen(false)}
        >
          <Icon icon="dazzle-linear-xmark" className="text-sm" />
        </Button>
        <div
          className={clsx("relative flex", proactiveChatIsOpen ? "w-max" : "w-0 overflow-hidden")}
        >
          <ChatBubble side={side} />
          <Arrow side={side} />
        </div>
      </div>
    </div>
  );
};

const Arrow = ({ side }: { side: "left" | "right" }) => {
  const baseClassName = [
    "absolute bottom-[20px] h-0 w-0 transform text-sm",
    "border-b-8  border-t-8",
    "border-b-transparent border-t-transparent",
  ];

  const shadowClassName =
    side === "left"
      ? "left-[-1px] border-r-[9px] border-r-gray-200 dark:border-r-gray-800"
      : "right-[-1px] border-l-[9px] border-l-gray-200 dark:border-l-gray-800";

  const arrowClassName =
    side === "left"
      ? "left-[1px] border-r-[8px] border-r-white dark:border-r-gray-700"
      : "right-[1px] border-l-[8px] border-l-white dark:border-l-gray-700";

  return (
    <>
      <div className={clsx(baseClassName, shadowClassName)}></div>
      <div className={clsx(baseClassName, arrowClassName)}></div>
    </>
  );
};

const ChatBubble = ({ side }: { side: "left" | "right" }) => {
  const getTranslation = useGetTranslation();
  const { config } = useLauncherStore(
    useShallow((state) => ({
      config: state.config,
    })),
  );

  const formattedDate = formatDistance(new Date(), new Date(), {
    addSuffix: true,
    locale: useIntl().locale,
  });

  return (
    <div
      className={clsx(
        "flex flex-col gap-2 rounded-xl bg-white p-3 shadow-md dark:bg-gray-700",
        "border border-gray-200 dark:border-gray-700",
        side === "left" ? "ml-2" : "mr-2 ",
      )}
    >
      <div className="flex items-center gap-1 overflow-hidden whitespace-nowrap text-[11px] text-gray-600 dark:text-gray-400">
        <span className="flex">
          {getTranslation(config?.settings.launcherSettings.nameDisplayed)}
        </span>
        <span>&middot;</span>
        <span>{formattedDate}</span>
      </div>
      <div className="w-max max-w-[300px] grow text-sm text-gray-800 dark:text-gray-200">
        {getTranslation(config?.settings.launcherSettings.message)}
      </div>
    </div>
  );
};
